import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import VerifiedIcon from "@mui/icons-material/Verified";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  Typography,
  Paper,
  Chip,
  Avatar,
  Button,
  alpha,
  LinearProgress,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { adminDataGridBoxSx, adminPageSx } from "../../theme/adminLayout";
import { API_BASE_URL } from "../../config/apiBase";

const VehicleManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const baseColors = tokens(theme.palette.mode);

  const colors = {
    ...baseColors,

    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },

    primary: {
      100: "#ffffff",
      200: "#fafafa",
      300: "#f5f5f5",
      400: "#ffffff",
      500: "#ffffff",
      600: "#e6e6e6",
      700: "#cccccc",
      800: "#b3b3b3",
      900: "#999999",
    },
  };

  const token = localStorage.getItem("accessToken");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    unverified: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/vehicle/getAllVehicles`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }
      const responseData = await response.json();

      // Check the new API response structure
      if (!responseData.status || !Array.isArray(responseData.result)) {
        throw new Error("Invalid response data format");
      }

      const formatDate = (isoDateString) => {
        const date = new Date(isoDateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      };

      // Map the data according to the new API response structure
      const mappedData = responseData.result.map((item) => ({
        id: item.Id,
        brand: item.carMake || "Unknown",
        model: item.model || "—",
        year: item.year || "N/A",
        registrationNumber: item.noPlate || "N/A",
        price: 0, // API doesn't provide price, using default
        fuelType: item.fuelType || "N/A",
        transmissionType: item.transmission || "N/A",
        isVerified: item.insuranceFileVerfied, // Assuming verified if insurance file exists
        vehicleApproved: item.vehicleApproved, // Add vehicleApproved field
        createdAt: formatDate(item.createdAt),
        sellerName: "Unknown Seller", // API doesn't provide seller name
        sellerId: item.SellerID || 0,
        status: "Listed",
        location: item.vehicleLocation || "N/A",
        seats: item.noOfSeats || "N/A",
        color: item.color || "N/A",
        vehicleType: item["VehicleType.type"] || "Car",
        hasAC: item.AcOrNon === "yes",
        hasGPS: item.gpsnavigation === "yes",
        mileage: item.mileage || "0",
        damageType: item.damageType || "None",
      }));

      const sortedData = mappedData.sort((a, b) => b.id - a.id);
      setData(sortedData);

      // Calculate statistics
      const totalVehicles = sortedData.length;
      const verifiedVehicles = sortedData.filter(
        (vehicle) => vehicle.isVerified
      ).length;

      setStats({
        total: totalVehicles,
        verified: verifiedVehicles,
        unverified: totalVehicles - verifiedVehicles,
      });
    } catch (error) {
      console.error("Error fetching vehicle data:", error);
      Swal.fire("Error!", "Failed to fetch vehicle data.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this vehicle listing!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.redAccent[500],
      cancelButtonColor: colors.grey[500],
      confirmButtonText: "Delete",
      background: "#ffffff",
      color: "#333333",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/vehicle/deleteVehicleByID/${id}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (!response.ok) {
            throw new Error("Failed to delete vehicle");
          }

          const updatedData = data.filter((item) => item.id !== id);
          setData(updatedData);

          // Update stats after deletion
          const deletedVehicle = data.find((item) => item.id === id);
          setStats((prev) => ({
            total: prev.total - 1,
            verified: deletedVehicle.isVerified
              ? prev.verified - 1
              : prev.verified,
            unverified: !deletedVehicle.isVerified
              ? prev.unverified - 1
              : prev.unverified,
          }));

          Swal.fire({
            title: "Deleted!",
            text: "The vehicle has been deleted.",
            icon: "success",
            background: "#ffffff",
            color: "#333333",
          });
        } catch (error) {
          console.error("Error deleting vehicle:", error);
          Swal.fire("Error!", "Failed to delete vehicle.", "error");
        }
      }
    });
  };

  const handleViewClick = (id) => {
    navigate(`/vehicle/${id}`);
  };

  // Get vehicle identifier for avatar
  const getVehicleIdentifier = (brand, model) => {
    return `${brand.charAt(0)}${model.charAt(0)}`.toUpperCase();
  };

  // Get avatar color based on brand - using blue shades
  const getAvatarColor = (brand) => {
    const blueShades = [
      "#22a3f1", // Main blue
      "#4db5f5", // Lighter blue
      "#1b82c1", // Darker blue
      "#67baf0", // Even lighter blue
      "#146291", // Even darker blue
    ];

    const charCodeSum = brand
      .split("")
      .reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return blueShades[charCodeSum % blueShades.length];
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    {
      field: "vehicleInfo",
      headerName: "Vehicle",
      flex: 1.8,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Avatar
            sx={{
              bgcolor: getAvatarColor(params.row.brand),
              width: 32,
              height: 32,
              mr: 1,
            }}
          >
            {getVehicleIdentifier(params.row.brand, params.row.model)}
          </Avatar>
          <Box>
            <Typography
              variant="body1"
              fontWeight="bold"
              fontSize="14px"
              color="#333333"
            >
              {`${params.row.brand} (${params.row.vehicleType})`}
            </Typography>
            <Typography variant="caption" color="#666666">
              {`${params.row.year} · ${params.row.registrationNumber}`}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "location",
      headerName: "Location",
      flex: 0.8,
      renderCell: (params) => (
        <Typography fontWeight="medium" color="#333333">
          {params.row.location}
        </Typography>
      ),
    },
    {
      field: "details",
      headerName: "Fuel Type",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Chip
            label={params.row.fuelType}
            size="small"
            sx={{
              mr: 0.5,
              backgroundColor: alpha(colors.blueAccent[500], 0.1),
              color: colors.blueAccent[600],
              fontSize: "11px",
            }}
          />
          <Chip
            label={params.row.transmissionType}
            size="small"
            sx={{
              backgroundColor: alpha(colors.greenAccent[500], 0.1),
              color: colors.greenAccent[600],
              fontSize: "11px",
            }}
          />
        </Box>
      ),
    },
    {
      field: "features",
      headerName: "Features",
      flex: 1,
      renderCell: (params) => (
        <Box>
          {params.row.hasAC && (
            <Chip
              label="AC"
              size="small"
              sx={{
                mr: 0.5,
                backgroundColor: alpha(colors.blueAccent[500], 0.1),
                color: colors.blueAccent[600],
                fontSize: "11px",
              }}
            />
          )}
          {params.row.hasGPS && (
            <Chip
              label="GPS"
              size="small"
              sx={{
                backgroundColor: alpha(colors.greenAccent[500], 0.1),
                color: colors.greenAccent[600],
                fontSize: "11px",
              }}
            />
          )}
          <Chip
            label={`${params.row.seats} seats`}
            size="small"
            sx={{
              ml: 0.5,
              backgroundColor: alpha(colors.blueAccent[300], 0.1),
              color: colors.blueAccent[600],
              fontSize: "11px",
            }}
          />
        </Box>
      ),
    },
    {
      field: "sellerId",
      headerName: "Seller ID",
      flex: 0.7,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color="#555555"
          sx={{
            cursor: "pointer",
            "&:hover": { color: colors.blueAccent[500] },
          }}
          onClick={() => navigate(`/seller/${params.row.sellerId}`)}
        >
          {params.row.sellerId}
        </Typography>
      ),
    },
    {
      field: "createdAt",
      headerName: "Listed On",
      flex: 0.9,
      renderCell: (params) => (
        <Typography variant="body2" color="#333333">
          {params.row.createdAt}
        </Typography>
      ),
    },
    {
      field: "vehicleApproved",
      headerName: "Approved",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.row.vehicleApproved ? "Approved" : "Pending"}
          size="small"
          sx={{
            fontWeight: "bold",
            borderRadius: "4px",
            backgroundColor: params.row.vehicleApproved
              ? alpha("#4CAF50", 0.2)
              : alpha("#FF9800", 0.2),
            color: params.row.vehicleApproved ? "#2E7D32" : "#E65100",
          }}
        />
      ),
    },
    // {
    //   field: "isVerified",
    //   headerName: "Status",
    //   flex: 0.7,
    //   renderCell: (params) => (
    //     <Chip
    //       icon={params.row.isVerified ? <VerifiedIcon /> : <DirectionsCarIcon />}
    //       label={params.row.isVerified ? "Verified" : "Unverified"}
    //       size="small"
    //       sx={{
    //         fontWeight: "bold",
    //         borderRadius: "4px",
    //         backgroundColor: params.row.isVerified
    //           ? alpha("#4CAF50", 0.2)
    //           : alpha("#FF9800", 0.2),
    //         color: params.row.isVerified ? "#2E7D32" : "#E65100",
    //         "& .MuiChip-icon": {
    //           color: "inherit",
    //         },
    //       }}
    //     />
    //   ),
    // },
    {
      field: "Actions",
      headerName: "Actions",
      flex: 0.6,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              onClick={() => handleViewClick(params.row.id)}
              sx={{
                backgroundColor: alpha(colors.blueAccent[500], 0.1),
                borderRadius: "4px",
                mr: 1,
                padding: "4px",
                "&:hover": {
                  backgroundColor: alpha(colors.blueAccent[500], 0.2),
                },
              }}
            >
              <VisibilityIcon
                sx={{ color: colors.blueAccent[500], fontSize: "18px" }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Vehicle">
            <IconButton
              onClick={() => handleDeleteClick(params.row.id)}
              sx={{
                backgroundColor: alpha(colors.redAccent[500], 0.1),
                borderRadius: "4px",
                padding: "4px",
                "&:hover": {
                  backgroundColor: alpha(colors.redAccent[500], 0.2),
                },
              }}
            >
              <DeleteIcon
                sx={{ color: colors.redAccent[500], fontSize: "18px" }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Custom toolbar component
  const CustomToolbar = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "6px 16px",
          borderBottom: `1px solid #e0e0e0`,
        }}
      >
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            size="small"
            sx={{
              borderColor: alpha(colors.blueAccent[500], 0.5),
              color: colors.blueAccent[500],
              mr: 2,
              "&:hover": {
                borderColor: colors.blueAccent[500],
                backgroundColor: alpha(colors.blueAccent[500], 0.1),
              },
            }}
          >
            Filter
          </Button>
          <GridToolbar
            sx={{
              "& .MuiButton-root": {
                color: "#757575",
                "&:hover": {
                  backgroundColor: alpha("#e0e0e0", 0.5),
                },
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={adminPageSx}>
      {/* Header with inline stats */}
      <Box
        mb={2}
        p={{ xs: 2, md: 2.5 }}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, #22a3f1 0%, #67baf0 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          color="white"
          sx={{ mb: 0.5, fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}
        >
          Vehicle Management
        </Typography>

        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="body1" color="white">
            Manage and monitor all vehicle listings in the system
          </Typography>

          {/* Inline stats */}
          <Stack direction="row" spacing={2} mt={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`Total: ${stats.total}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "white",
                px: 1,
              }}
            />
            {/* <Chip
              icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
              label={`Verified: ${stats.verified}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(76, 175, 80, 0.3)",
                color: "white",
                px: 1,
                "& .MuiChip-icon": {
                  color: "white",
                },
              }}
            /> */}
            {/* <Chip
              icon={<DirectionsCarIcon sx={{ fontSize: 14 }} />}
              label={`Pending: ${stats.unverified}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(255, 152, 0, 0.3)",
                color: "white",
                px: 1,
                "& .MuiChip-icon": {
                  color: "white",
                },
              }}
            /> */}
          </Stack>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#ffffff",
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
        }}
      >
        <Box
          sx={{
            ...adminDataGridBoxSx,
            overflowX: "auto",
            "& .MuiDataGrid-root": {
              border: "none",
              fontSize: "13px",
              color: "#333333",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid #e0e0e0`,
              padding: "12px",
              fontSize: "13px",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#22a3f1",
              borderBottom: "none",
              fontSize: "14px",
              fontWeight: "bold",
              padding: "0 12px",
              color: "white",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: "#ffffff",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: "rgba(34, 163, 241, 0.05)",
              padding: "6px 12px",
              color: "#333333",
            },
            "& .MuiDataGrid-row": {
              "&:hover": {
                backgroundColor: "rgba(34, 163, 241, 0.1)",
              },
            },
            "& .MuiTablePagination-root": {
              color: "#333333",
            },
            "& .MuiIconButton-root": {
              color: "#22a3f1",
            },
            "& .MuiCheckbox-root": {
              color: "#22a3f1",
            },
            "& .MuiSvgIcon-root": {
              "&.MuiSelect-icon": {
                color: "#757575",
              },
            },
            "& .MuiMenuItem-root": {
              color: "#333333",
            },
          }}
        >
          <DataGrid
            rows={data}
            columns={columns}
            columnVisibilityModel={{
              id: !isMobile,
              location: !isMobile,
              details: !isMobile,
              features: !isMobile,
              sellerId: !isMobile,
              createdAt: !isMobile,
            }}
            components={{
              Toolbar: CustomToolbar,
              LoadingOverlay: LinearProgress,
            }}
            loading={loading}
            disableSelectionOnClick
            rowHeight={56}
            pageSize={8}
            rowsPerPageOptions={[8, 16, 32]}
            sx={{
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default VehicleManagement;